import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, apiError, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Trash2, FileText, Download } from "lucide-react";
import { localeOf } from "../../i18n";

const emptyMission = { title: "", description: "", proof_type: "photo", points: 5, required: true, instructions: "", proof_expected: "", due_at: "" };

export default function StaffEventDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [mForm, setMForm] = useState(emptyMission);
  const [mOpen, setMOpen] = useState(false);
  const [mEditId, setMEditId] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const locale = localeOf(i18n.language);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const load = useCallback(() => Promise.all([
    api.get(`/events/${id}`).then(({ data }) => setEvent(data)),
    api.get(`/events/${id}/leads-summary`).then(({ data }) => setLeadStats(data)).catch(() => {}),
  ]), [id]);
  useEffect(() => { load(); }, [load]);

  const exportLeads = async () => {
    try {
      const { data } = await api.get(`/events/${id}/leads.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${id}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("fieldLeads.exported"));
    } catch (e) {
      toast.error(e?.response?.status === 403 ? t("fieldLeads.forbidden") : apiError(e));
    }
  };

  const saveMission = async () => {
    const payload = { ...mForm, points: Number(mForm.points) || 0 };
    try {
      if (mEditId) await api.patch(`/missions/${mEditId}`, payload);
      else await api.post(`/events/${id}/missions`, payload);
      toast.success(t("common.saved"));
      setMOpen(false); setMForm(emptyMission); setMEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteMission = async (mid) => {
    try { await api.delete(`/missions/${mid}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const setAttendance = async (pid, status, checked_in) => {
    try {
      await api.patch(`/participations/${pid}/attendance`, { status, checked_in, reason: "correction" });
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!event) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  const checkinUrl = `${window.location.origin}/checkin?code=${event.short_code}`;

  return (
    <div className="space-y-6" data-testid="staff-event-detail">
      <div>
        <h1 className="font-display text-3xl font-extrabold" data-testid="staff-event-title">{event.title}</h1>
        <p className="text-sm text-muted-foreground">
          {fmtDate(event.starts_at, locale)} · {event.location} · {event.campaign_name || t("common.none")}
        </p>
        <Link to={`/staff/events/${id}/day-summary`} data-testid="open-day-summary-link"
          className="inline-flex h-11 items-center gap-2 rounded-full border border-input px-5 font-bold">
          <FileText className="h-4 w-4" />{t("daySummary.title")}
        </Link>
        <Link to={`/staff/events/${id}/report`} data-testid="open-partner-report-link"
          className="mt-3 inline-flex h-11 items-center rounded-full bg-accent px-5 text-sm font-bold text-accent-foreground">
          <FileText className="mr-2 h-4 w-4" />{t("report.title")}
        </Link>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="vy-card p-5 text-center" data-testid="event-qr-card">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("checkin.scan")}</p>
          <div className="mt-3 flex justify-center">
            <QRCodeSVG value={checkinUrl} size={148} />
          </div>
          <p className="mt-3 font-display text-2xl font-extrabold tracking-[0.25em]" data-testid="event-short-code">
            {event.short_code}
          </p>
          <p className="text-xs text-muted-foreground">{t("events.shortCode")}</p>
        </div>
        <div className="vy-card p-5">
          <p className="text-xs font-bold uppercase text-muted-foreground">{t("events.capacity")}</p>
          <p className="mt-2 font-display text-3xl font-extrabold">{event.confirmed_count}/{event.capacity}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {event.waitlist_count} {t("events.waitlisted").toLowerCase()} · {event.checked_in_count} {t("events.checkedIn")}
          </p>
        </div>
        <div className="vy-card p-5">
          <p className="text-xs font-bold uppercase text-muted-foreground">{t("admin.attendancePoints")}</p>
          <p className="mt-2 font-display text-3xl font-extrabold">{event.attendance_points}</p>
          {event.partner && <p className="mt-1 text-sm text-muted-foreground">{event.partner.name}</p>}
        </div>
      </div>

      <section className="vy-card p-5" data-testid="staff-missions-section">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold">{t("events.missions")}</h2>
          {isAdmin && (
            <Button data-testid="new-mission-btn" className="h-11 rounded-full font-bold"
              onClick={() => { setMForm(emptyMission); setMEditId(null); setMOpen(true); }}>
              <Plus className="mr-1 h-4 w-4" />{t("admin.newMission")}
            </Button>
          )}
        </div>
        <ul className="mt-3 divide-y divide-border">
          {(event.missions || []).map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-3" data-testid={`staff-mission-${m.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{m.title}</p>
                <p className="text-xs text-muted-foreground">
                  {t(`missions.${m.proof_type}`)} · {m.points} {t("common.points")}
                </p>
              </div>
              {isAdmin && (
                <>
                  <Button variant="ghost" className="h-10 rounded-full" data-testid={`edit-mission-btn-${m.id}`}
                    onClick={() => { setMEditId(m.id); setMForm({ ...emptyMission, ...m }); setMOpen(true); }}>
                    {t("common.edit")}
                  </Button>
                  <Button variant="ghost" className="h-10 rounded-full text-destructive"
                    data-testid={`delete-mission-btn-${m.id}`} onClick={() => deleteMission(m.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </li>
          ))}
          {(event.missions || []).length === 0 && (
            <li className="py-3 text-sm text-muted-foreground">{t("common.none")}</li>
          )}
        </ul>
      </section>

      <section className="vy-card p-5" data-testid="staff-participants-section">
        <h2 className="font-display text-lg font-bold">{t("events.participants")}</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="py-2">{t("admin.name")}</th>
                <th className="py-2">{t("status.confirmed")}</th>
                <th className="py-2">{t("checkin.title")}</th>
                <th className="py-2">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {(event.participants || []).map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`participant-row-${p.id}`}>
                  <td className="py-3">
                    <p className="font-semibold">{p.user_name}</p>
                    <p className="text-xs text-muted-foreground">{p.user_email}</p>
                  </td>
                  <td className="py-3"><StatusBadge status={p.status} /></td>
                  <td className="py-3 text-xs text-muted-foreground">
                    {p.checked_in_at ? `${fmtDate(p.checked_in_at, locale)} (${p.checkin_method})` : "—"}
                  </td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid={`mark-present-btn-${p.id}`} variant="outline"
                        className="h-10 rounded-full text-xs font-bold"
                        onClick={() => setAttendance(p.id, "confirmed", true)}>
                        {t("review.markPresent")}
                      </Button>
                      <Button data-testid={`mark-noshow-btn-${p.id}`} variant="outline"
                        className="h-10 rounded-full text-xs font-bold text-destructive"
                        onClick={() => setAttendance(p.id, "no_show", false)}>
                        {t("review.markNoShow")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {(event.participants || []).length === 0 && (
                <tr><td colSpan={4} className="py-4 text-sm text-muted-foreground">{t("common.none")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="vy-card p-5" data-testid="field-leads-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold">{t("fieldLeads.title")}</h2>
          <Button data-testid="export-leads-btn" variant="outline"
            className="h-11 rounded-full font-bold" onClick={exportLeads}>
            <Download className="mr-2 h-4 w-4" />{t("fieldLeads.export")}
          </Button>
        </div>
        {leadStats && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[["total", leadStats.total], ["qr", leadStats.qr_self_service],
              ["manual", leadStats.manual_ambassador], ["offline", leadStats.captured_offline],
              ["pending", leadStats.pending_sync], ["confirmed", leadStats.confirmed]].map(([k, v]) => (
              <div key={k} className="rounded-xl bg-muted p-3" data-testid={`lead-stat-${k}`}>
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {t(`fieldLeads.${k}`)}
                </p>
                <p className="mt-1 font-display text-2xl font-extrabold">{v}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent data-testid="mission-form-dialog">
          <DialogHeader><DialogTitle>{mEditId ? t("common.edit") : t("admin.newMission")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="mission-title-input" className="h-12" placeholder={t("admin.name")}
              value={mForm.title} onChange={(e) => setMForm({ ...mForm, title: e.target.value })} />
            <Textarea data-testid="mission-description-input" rows={2} placeholder={t("events.objectives")}
              value={mForm.description || ""} onChange={(e) => setMForm({ ...mForm, description: e.target.value })} />
            <Textarea data-testid="mission-instructions-input" rows={3} placeholder={t("missions.instructions")}
              value={mForm.instructions || ""} onChange={(e) => setMForm({ ...mForm, instructions: e.target.value })} />
            <Input data-testid="mission-proof-expected-input" className="h-12" placeholder={t("missions.proofExpected")}
              value={mForm.proof_expected || ""} onChange={(e) => setMForm({ ...mForm, proof_expected: e.target.value })} />
            <div>
              <label className="text-xs font-bold">{t("missions.deadline")}</label>
              <Input data-testid="mission-due-input" type="datetime-local" className="h-12"
                value={(mForm.due_at || "").slice(0, 16)}
                onChange={(e) => setMForm({ ...mForm, due_at: e.target.value })} />
            </div>
            <select data-testid="mission-type-select" className="h-12 w-full rounded-xl border border-input px-3"
              value={mForm.proof_type} onChange={(e) => setMForm({ ...mForm, proof_type: e.target.value })}>
              <option value="photo">{t("missions.photo")}</option>
              <option value="note">{t("missions.note")}</option>
              <option value="completion">{t("missions.completion")}</option>
            </select>
            <div>
              <label className="text-xs font-bold">{t("admin.missionPoints")}</label>
              <Input data-testid="mission-points-input" type="number" className="h-12" value={mForm.points}
                onChange={(e) => setMForm({ ...mForm, points: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="mission-save-btn" onClick={saveMission} className="h-12 rounded-full font-bold">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
